/**
 * Order-specific detail sections: key-facts strip, Customer / Shipping /
 * Payment / Fulfillment rows, and order-level flag chips. The label/value
 * rows are react-admin <SimpleShowLayout> blocks reading from the order
 * record context via `source` paths; composite/derived values use
 * <FunctionField>. Key facts and flag chips stay on the shared kit's bespoke
 * primitives. Presents a partner-facing subset of reware-admin's order card — see OrderExpand.
 */
import { FunctionField, SimpleShowLayout, TextField } from 'react-admin';
import { Box } from '@mui/material';

import {
  detailLayoutSx,
  displayNameFor,
  type Flag,
  FlagChips,
  formatDate,
  type KeyFact,
  KeyFactsStrip,
  monoFieldSx,
  SectionLabel,
} from '@src/components/indexPage';

import { formatCardLastFour, formatMoney } from './format';
import { present } from './types';
import type { ConstantOption, OrderRecord } from './types';

const activeItemCount = (order: OrderRecord): number =>
  present(order.orderItems).filter(item => !item.removedAt).length;

export const OrderKeyFacts = ({ order, states }: { order: OrderRecord, states: ConstantOption[] }) => {
  const currency = order.transactionCurrencyCode;
  const facts: KeyFact[] = [
    { label: 'Total', value: formatMoney(order.total, currency) },
    { label: 'Items', value: activeItemCount(order) },
    { label: 'Status', value: displayNameFor(states, order.state) },
    { label: 'Payment', value: order.orderCapturedAt ? 'Captured' : 'Uncaptured' },
    { label: 'Placed', value: formatDate(order.orderPlacedAt) },
    { label: 'Warehouse', value: order.saleWarehouse?.code ?? '—' },
  ];
  return <KeyFactsStrip facts={facts} />;
};

export const CustomerSection = ({ order }: { order: OrderRecord }) => {
  const address = order.deliveryAddress;
  return (
    <Box>
      <SectionLabel>Customer</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <TextField source="deliveryAddress.name" label="Name" emptyText="—" />
        <TextField source="deliveryAddress.email" label="Email" emptyText="—" />
        {address?.phone ? <TextField source="deliveryAddress.phone" label="Phone" /> : null}
      </SimpleShowLayout>
    </Box>
  );
};

export const ShippingSection = ({ order }: { order: OrderRecord }) => {
  const address = order.deliveryAddress;
  const isInstore = order.saleWarehouse?.purpose === 'store';
  const residential = order.ypackages?.[0]?.isResidential;
  const cityState = [address?.city, address?.state].filter(Boolean).join(', ') || null;

  if (isInstore) {
    return (
      <Box>
        <SectionLabel>In-Store</SectionLabel>
        <SimpleShowLayout sx={detailLayoutSx}>
          <TextField source="saleWarehouse.code" label="Store Code" emptyText="—" />
          <TextField source="saleWarehouse.name" label="Store Name" emptyText="—" />
          <TextField source="salesAssociate" label="Sales Associate" emptyText="—" />
        </SimpleShowLayout>
      </Box>
    );
  }

  return (
    <Box>
      <SectionLabel>Shipping</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <TextField source="deliveryAddress.address1" label="Address" emptyText="—" />
        {address?.address2 ? <TextField source="deliveryAddress.address2" label="Address 2" /> : null}
        <FunctionField label="City / State" render={() => cityState ?? '—'} />
        <TextField source="deliveryAddress.postalCode" label="Postal Code" sx={monoFieldSx} emptyText="—" />
        <TextField source="deliveryAddress.country" label="Country" emptyText="—" />
        <TextField source="shippingLevel" label="Shipping Level" emptyText="—" />
        {residential != null ? (
          <FunctionField label="Residential" render={() => (residential ? 'Yes' : 'No')} />
        ) : null}
      </SimpleShowLayout>
    </Box>
  );
};

export const PaymentSection = ({ order }: { order: OrderRecord }) => {
  const payment = order.paymentDetails;
  const currency = order.transactionCurrencyCode;
  const method = payment?.ccBrand ?
    `${payment.ccBrand} ···· ${formatCardLastFour(payment.ccLast4)}`.trim() :
    payment?.fundingSource ?? null;
  return (
    <Box>
      <SectionLabel>Payment</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <FunctionField label="Method" render={() => method ?? '—'} />
        <FunctionField label="Captured" render={() => (order.orderCapturedAt ? formatDate(order.orderCapturedAt) : 'Not captured')} />
        <FunctionField label="Original Total" render={() => formatMoney(payment?.originalTotal ?? order.total, currency)} />
        {payment?.refundedTotal ? (
          <FunctionField label="Refunded" render={() => formatMoney(payment.refundedTotal, currency)} />
        ) : null}
        <FunctionField label="Remaining" render={() => formatMoney(payment?.remainingTotal ?? order.total, currency)} />
        {currency && currency !== 'USD' ? <FunctionField label="Currency" render={() => currency} /> : null}
        <FunctionField label="Returnable Until" render={() => formatDate(order.returnableUntil)} />
      </SimpleShowLayout>
    </Box>
  );
};

export const FulfillmentSection = ({ order }: { order: OrderRecord }) => {
  const platformLabel = order.commercePlatform === 'shopify' ? 'Shopify' : order.commercePlatform === 'legacy' ? 'Trove' : order.commercePlatform;
  const warehouseText = order.saleWarehouse ?
    `${order.saleWarehouse.code ?? ''}${order.saleWarehouse.typeDisplay ? ` (${order.saleWarehouse.typeDisplay})` : ''}`.trim() :
    null;
  return (
    <Box>
      <SectionLabel>Fulfillment</SectionLabel>
      <SimpleShowLayout sx={detailLayoutSx}>
        <FunctionField label="Warehouse" render={() => warehouseText ?? '—'} />
        <FunctionField label="Commerce Platform" render={() => platformLabel ?? '—'} />
        {order.externalOrderNumber ? <TextField source="externalOrderNumber" label="External Order #" sx={monoFieldSx} /> : null}
        {order.salesAssociate ? <TextField source="salesAssociate" label="Sales Associate" /> : null}
        {present(order.ypackages).length ? <FunctionField label="Packages" render={() => present(order.ypackages).length} /> : null}
        {present(order.returnYpackages).length ? <FunctionField label="Return Packages" render={() => present(order.returnYpackages).length} /> : null}
      </SimpleShowLayout>
    </Box>
  );
};

/** Order-level flag chips — rendered only when true. */
export const OrderFlags = ({ order }: { order: OrderRecord }) => {
  const flags: Flag[] = [
    { label: 'Order Locked', color: 'warning', show: !!order.isOrderLocked, title: "Locked after order is created in 3PL's WMS" },
    { label: '3PL Stalled', color: 'error', show: !!order.is3plOrderStalled },
    { label: 'Webhook Error', color: 'error', show: !!order.orderRecentWebhookEventError, title: order.orderRecentWebhookEventError ?? undefined },
    { label: 'Has Escalations', color: 'warning', show: present(order.escalations).length > 0 },
    { label: 'Has Returns', color: 'default', show: present(order.returnYpackages).length > 0 },
    { label: 'Uncaptured Payment', color: 'warning', show: !order.orderCapturedAt && order.state !== 'CANCELED' },
  ];
  return <FlagChips flags={flags} />;
};
